import * as React from 'react';
import {
  Box,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

export default function DateTimePicker({
  onChange,
  minYear,
  maxYear,
  restrict = "none",
  defaultValue,
  monthFormat = "full",
  size = "medium",
  label = "Date & Time",
  order = ["month", "day", "year"],
  gap = 2,
  controlWidths = { month: 4, day: 4, year: 4, hour: 4, minute: 4, ampm: 4 },
  placeholders = { month: "Month", day: "Day", year: "Year", hour: "Hour", minute: "Minute", ampm: "AM/PM" },
  minuteStep = 1, // customizable increments
}) {
  const [month, setMonth] = React.useState('');
  const [day, setDay] = React.useState('');
  const [year, setYear] = React.useState('');
  const [hour, setHour] = React.useState('');
  const [minute, setMinute] = React.useState('');
  const [ampm, setAmpm] = React.useState('');

  const currentYear = new Date().getFullYear();

  // Parse defaultValue
  React.useEffect(() => {
    if (!defaultValue) {
      setYear(''); setMonth(''); setDay('');
      setHour(''); setMinute(''); setAmpm('');
    } else {
      const dateObj = typeof defaultValue === "string" ? new Date(defaultValue) : defaultValue;
      if (!isNaN(dateObj)) {
        setYear(dateObj.getFullYear());
        setMonth(dateObj.getMonth() + 1);
        setDay(dateObj.getDate());

        let h = dateObj.getHours();
        const ampmVal = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12; // convert to 12-hour
        setHour(String(h).padStart(2, '0'));
        setMinute(String(dateObj.getMinutes()).padStart(2, '0'));
        setAmpm(ampmVal);
      }
    }
  }, [defaultValue]);

  // Year range logic
  let computedMinYear = minYear ?? 1900;
  let computedMaxYear = maxYear ?? currentYear;
  if (restrict === "past") {
    computedMaxYear = maxYear ?? currentYear;
  } else if (restrict === "future") {
    computedMinYear = minYear ?? currentYear;
    computedMaxYear = maxYear ?? currentYear + 100;
  }
  const years = Array.from({ length: computedMaxYear - computedMinYear + 1 }, (_, i) => computedMinYear + i);

  const monthsFull = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthsShort = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const months = monthFormat === "short" ? monthsShort : monthsFull;

  const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
  const getDaysInMonth = (m, y) => {
    if (!m) return 31;
    switch (m) {
      case 2: return isLeapYear(y) ? 29 : 28;
      case 4: case 6: case 9: case 11: return 30;
      default: return 31;
    }
  };
  const days = Array.from({ length: getDaysInMonth(month, year) }, (_, i) => i + 1);

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minutes = Array.from({ length: 60 / minuteStep }, (_, i) => String(i * minuteStep).padStart(2, '0'));
  const ampmOptions = ["AM", "PM"];

  // Emit combined datetime
  React.useEffect(() => {
    if (month && day && year && hour && minute && ampm) {
      let h = parseInt(hour, 10);
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;

      const dateObj = new Date(year, month - 1, day, h, parseInt(minute, 10));
      onChange?.({ dateObj, formatted: dateObj.toISOString() });
    } else {
      onChange?.(null);
    }
  }, [month, day, year, hour, minute, ampm, onChange]);

  const selectSize = size === "small" ? "small" : "medium";
  const customStyle = size === "large" ? { fontSize: "1.25rem", padding: "12px" } : {};

  const renderControl = (type) => {
    const width = controlWidths[type] ?? 4;
    const commonProps = {
      fullWidth: true,
      size: selectSize,
      sx: customStyle,
      displayEmpty: true,
    };

    switch (type) {
      case "month":
        return (
          <Grid key="month" item xs={width}>
            <Select {...commonProps} value={month || ""} onChange={(e) => setMonth(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.month}</span> : months[selected - 1]}>
              {months.map((m, idx) => <MenuItem key={m} value={idx + 1}>{m}</MenuItem>)}
            </Select>
          </Grid>
        );
      case "day":
        return (
          <Grid key="day" item xs={width}>
            <Select {...commonProps} value={day || ""} onChange={(e) => setDay(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.day}</span> : selected}>
              {days.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </Grid>
        );
      case "year":
        return (
          <Grid key="year" item xs={width}>
            <Select {...commonProps} value={year || ""} onChange={(e) => setYear(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.year}</span> : selected}>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </Grid>
        );
      case "hour":
        return (
          <Grid key="hour" item xs={width}>
            <Select {...commonProps} value={hour || ""} onChange={(e) => setHour(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.hour}</span> : selected}>
              {hours.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
            </Select>
          </Grid>
        );
      case "minute":
        return (
          <Grid key="minute" item xs={width}>
            <Select {...commonProps} value={minute || ""} onChange={(e) => setMinute(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.minute}</span> : selected}>
              {minutes.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </Grid>
        );
      case "ampm":
        return (
          <Grid key="ampm" item xs={width}>
            <Select {...commonProps} value={ampm || ""} onChange={(e) => setAmpm(e.target.value)}
              renderValue={(selected) => selected === "" ? <span style={{ color: '#999' }}>{placeholders.ampm}</span> : selected}>
              {ampmOptions.map((ap) => <MenuItem key={ap} value={ap}>{ap}</MenuItem>)}
            </Select>
          </Grid>
        );
      default:
        return null;
    }
  };

  const isPartialSelection =
    (month || day || year || hour || minute || ampm) &&
    !(month && day && year && hour && minute && ampm);

return (
  <Box>
    <FormLabel sx={{ fontSize: '0.95rem', color: 'text.secondary' }}>
      {label}
    </FormLabel>

    {/* Weekday caption */}
    {month && day && year && (
      <Typography variant="body2" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
        {new Date(year, month - 1, day).toLocaleDateString(undefined, { weekday: 'long' })}
      </Typography>
    )}

    {/* Date Selects */}
    <Grid container spacing={gap}>
      {order.map((field) => renderControl(field))}
    </Grid>

    {/* Time Selects */}
    <Grid container spacing={gap} sx={{ mt: 1 }}>
      {["hour", "minute", "ampm"].map((field) => renderControl(field))}
    </Grid>

    {/* Warning for incomplete selection */}
    {isPartialSelection && (
      <Typography variant="body2" color="warning" sx={{ mt: 0.5 }}>
        Please complete selection of day, month, year, hour, minute, and AM/PM.
      </Typography>
    )}
  </Box>
);
}
